import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { createFocusTrap } from 'focus-trap'
import { popups, fullBleedEls } from './elements.js'

let focusTrap
let popup

/**
 * Remove :focus outline unless user is using the keyboard to navigate
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.1
 */
export function handleFirstTab(e) {
	if (e.key === 'Tab') {
		document.body.classList.add('u-user-is-tabbing')
		window.removeEventListener('keydown', handleFirstTab)
		window.addEventListener('mousedown', handleMouseDownOnce)
	}
}

function handleMouseDownOnce() {
	document.body.classList.remove('u-user-is-tabbing')
	window.removeEventListener('mousedown', handleMouseDownOnce)
	window.addEventListener('keydown', handleFirstTab)
}

/**
 * Fix full-bleed overflow with visible scrollbars
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.1
 */
export function scrollbarFix() {
	// Create and append a temp div to measure
	const scrollDiv = document.createElement('div')
	scrollDiv.className = 'u-scrollbar-measure'
	document.body.appendChild(scrollDiv)

	// Get the scrollbar width
	const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth

	// Delete the DIV
	document.body.removeChild(scrollDiv)

	if (scrollbarWidth > 0) {
		fullBleedEls.forEach((el) => {
			el.style.setProperty(
				'--full-bleed-width',
				`calc(100vw - ${scrollbarWidth}px)`
			)
			el.style.setProperty(
				'--full-bleed-margin',
				`calc(50% - 50vw + ${scrollbarWidth / 2}px)`
			)
		})
	}
}

/**
 * Disable animations on resize
 *
 * @author     Andy Bell
 * @version    1.0.0
 * @since      2022.1
 * @see        https://css-tricks.com/stop-animations-during-window-resizing/
 */

let resizeTimer
export function disableAnimationOnResize() {
	document.body.classList.add('u-resize-animation-stopper')
	clearTimeout(resizeTimer)
	resizeTimer = setTimeout(() => {
		document.body.classList.remove('u-resize-animation-stopper')
	}, 400)
}

/**
 * Toggle popup
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2022.1
 */
export function togglePopupVisibility(e) {
	e.preventDefault()
	const target = e.currentTarget.getAttribute('data-target')
	popup = document.querySelector(`.js-popup[data-popup="${target}"]`)
	const state = popup.getAttribute('aria-hidden') === 'true' || false
	if (state) {
		focusTrap = createFocusTrap(`.js-popup[data-popup="${target}"]`)
		setTimeout(function () {
			focusTrap.activate()
		}, 150)
		disableBodyScroll(popup)
	} else {
		focusTrap.deactivate()
		enableBodyScroll(popup)
	}
	popup.setAttribute('aria-hidden', !state)
}

/**
 * Hide popup on overlay click and ESC key press
 *
 * @author      Mario Hoyos
 * @version     1.0.0
 * @since       2022.1
 */
export function closePopup(e) {
	if (popup.getAttribute('aria-hidden') === 'false') {
		if (e.keyCode === 27) {
			popups.forEach((item) => {
				item.setAttribute('aria-hidden', 'true')
				focusTrap.deactivate()
				enableBodyScroll(popup)
			})
		} else if (
			!e.currentTarget.querySelector('.js-popup-wrapper').contains(e.target)
		) {
			e.currentTarget.setAttribute('aria-hidden', 'true')
			focusTrap.deactivate()
			enableBodyScroll(popup)
		}
	}
}

/**
 * Collapse section with height animation
 *
 * @author      Brandon Smith
 * @version     1.0.0
 * @since       2022.1
 * @see         https://codepen.io/brundolf/pen/dvoGyw
 */
export function collapseSection(
	element,
	expandedSibling = false,
	collapsedHeight = 0
) {
	// Get the height of the element's inner content, regardless of its actual size
	const sectionHeight = element.scrollHeight

	// Temporarily disable all css transitions
	const elementTransition = element.style.transition
	element.style.transition = ''

	// On the next frame (as soon as the previous style change has taken effect),
	// explicitly set the element's height to its current pixel height, so we
	// aren't transitioning out of 'auto'
	requestAnimationFrame(function () {
		element.style.height = `${sectionHeight}px`
		element.style.transition = elementTransition

		// On the next frame (as soon as the previous style change has taken effect),
		// have the element transition to height: 0
		requestAnimationFrame(function () {
			element.style.height = `${collapsedHeight}px`
		})
	})

	// Mark the section as "currently collapsed"
	element.setAttribute('data-collapsed', 'true')

	// If is a second level menu item collapse the parent submenu (because of avoerflow:hidden)
	const elementParent = element.parentNode.parentNode
	if (elementParent.classList.contains('sub-menu') && !expandedSibling) {
		elementParent.style.height = `${
			elementParent.offsetHeight - sectionHeight
		}px`
	}
}

/**
 * Expand section with height animation
 *
 * @author      Brandon Smith
 * @version     1.0.0
 * @since       2022.1
 * @see         https://codepen.io/brundolf/pen/dvoGyw
 */
export function expandSection(element, expandedSibling = false) {
	// Get the height of the element's inner content, regardless of its actual size
	const sectionHeight = element.scrollHeight

	// Have the element transition to the height of its inner content
	element.style.height = `${sectionHeight}px`

	// Mark the section as "currently not collapsed"
	element.setAttribute('data-collapsed', 'false')

	// If is a second level menu item expand the parent submenu (because of avoerflow:hidden)
	const elementParent = element.parentNode.parentNode
	if (elementParent.classList.contains('sub-menu')) {
		// If there are siblings open calc the height diff
		if (expandedSibling) {
			const heightDiff =
				sectionHeight - expandedSibling.nextElementSibling.scrollHeight
			elementParent.style.height = `${
				elementParent.offsetHeight + heightDiff
			}px`
			// If nothing is open just use the submenu heigh
		} else {
			elementParent.style.height = `${
				elementParent.offsetHeight + sectionHeight
			}px`
		}
	}
}

/**
 * Handle card click to make the whole element clickable allowing inside links
 *
 * @author     Mario Hoyos
 * @version    1.0.0
 * @since      2022.1
 */
export function handleCardClick(e) {
	const isTextSelected = window.getSelection().toString()
	const cardLink = e.target.closest('.js-card').querySelector('.js-main-link')
	const clickableElements = e.target.closest('.js-card').querySelectorAll('a')
	clickableElements.forEach((el) =>
		el.addEventListener('click', (ev) => ev.stopPropagation())
	)
	if (!isTextSelected) {
		cardLink.click()
	}
}

/**
 * Animated scroll to anchor detection
 *
 * @author     Jeff Starr
 * @version    1.0.0
 * @since      2022.1
 * @see        https://perishablepress.com/vanilla-javascript-scroll-anchor/
 */
function scrollTo() {
	const links = document.querySelectorAll('.js-scroll')
	links.forEach((each) => (each.onclick = scrollAnchors))
}

/**
 * Animated scroll to anchor
 *
 * @author     Jeff Starr
 * @version    1.0.0
 * @since      2022.1
 * @see        https://perishablepress.com/vanilla-javascript-scroll-anchor/
 */
function scrollAnchors(e, respond = null) {
	const distanceToTop = (el) => Math.floor(el.getBoundingClientRect().top)
	e.preventDefault()
	const targetID = respond
		? respond.getAttribute('href')
		: this.getAttribute('href')
	const targetAnchor = document.querySelector(targetID)
	if (!targetAnchor) return
	const originalTop = distanceToTop(targetAnchor)
	window.scrollBy({ top: originalTop, left: 0, behavior: 'smooth' })
	const checkIfDone = setInterval(function () {
		const atBottom =
			window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 2
		if (distanceToTop(targetAnchor) === 0 || atBottom) {
			targetAnchor.tabIndex = '-1'
			targetAnchor.focus()
			window.history.pushState('', '', targetID)
			clearInterval(checkIfDone)
		}
	}, 100)
}
